"use client";

import * as X from "@/ui";
import { useParams } from "next/navigation";
import { localeNames } from "@/config";
import { useLocale } from "next-intl";
import NonIntlLink from "next/link";

import GlobeImageBlack from "./assets/locale-globe-black.svg";
import GlobeImageWhite from "./assets/locale-globe-white.svg";


export function LocaleSelectorDesktop(props: X.PageProps<{
  hoverColor: "black" | "white";
  className?: string;
}>) {
  const { intlHrefs, nonIntlHrefs, currentLocaleName } = useLocaleSelector(props);

  const linkStyle = X.clsx(
    "block px-8 py-2",
    "text-gray hover:text-black text-[12px]",
    props.hoverColor === "white" ? "hover:text-white bg-black" : "hover:text-black",
  );

  return (
    <div className={X.clsx("relative group/language text-[12px] pl-4", props.className)}>
      <button className="flex items-center space-x-1 py-2">
        <X.Image
          src={props.hoverColor === "white" ? GlobeImageWhite : GlobeImageBlack}
          alt="Globe"
          className="w-[16px] h-auto"
        />
        <span className="pl-[4px]">{currentLocaleName}</span>
        <X.Icon.ChevronDown className="h-4 w-4" />
      </button>

      <div className={X.clsx(
        "absolute hidden group-hover/language:flex",
        "flex-col top-full left-0 rounded-md pb-2 w-32 z-10 px-2",
      )}>
        {intlHrefs.map(({ locale, name, href }) => (
          <X.Link
            key={locale}
            href={href}
            rel="alternate"
            locale={locale as X.Locale}
            hrefLang={locale}
            className={linkStyle}
          >
            {name}
          </X.Link>
        ))}
        {nonIntlHrefs.map(({ locale, name, href }) => (
          <NonIntlLink
            key={locale}
            href={href}
            rel="alternate"
            hrefLang={locale}
            className={linkStyle}
          >
            {name}
          </NonIntlLink>
        ))}
      </div>
    </div>
  );
}


export function LocaleSelectorMobile(props: X.PageProps<{
  backgroundColor: "black" | "white";
  className?: string;
}>) {
  const { intlHrefs, nonIntlHrefs, currentLocaleName } = useLocaleSelector(props);
  const [isOpen, setIsOpen] = X.useState(false);
  const linkStyle = X.clsx("block px-4 py-2 text-gray text-[12px]");

  return (
    <div className={X.clsx("relative text-[12px]", props.className)}>
      <button className="flex items-center space-x-1 py-2" onClick={() => setIsOpen(wasOpen => !wasOpen)}>
        <X.Image
          src={props.backgroundColor === "white" ? GlobeImageBlack : GlobeImageWhite}
          alt="Globe"
          className="w-[16px] h-auto"
        />
        <span className="pl-[4px]">{currentLocaleName}</span>
        <X.Icon.ChevronDown className="h-4 w-4" />
      </button>

      <div className={X.clsx(
        "absolute flex-col top-full left-0 rounded-md pb-2 w-32 z-10 px-2",
        isOpen ? "flex" : "hidden",
        props.backgroundColor === "white" ? "bg-white" : "bg-black",
      )}>
        {intlHrefs.map(({ locale, name, href }) => (
          <X.Link
            key={locale}
            rel="alternate"
            locale={locale as X.Locale}
            hrefLang={locale}
            className={linkStyle}
            href={href}
          >
            {name}
          </X.Link>
        ))}
        {nonIntlHrefs.map(({ locale, name, href }) => (
          <NonIntlLink
            key={locale}
            rel="alternate"
            hrefLang={locale}
            className={linkStyle}
            href={href}
          >
            {name}
          </NonIntlLink>
        ))}
      </div>
    </div>
  );
}


function useLocaleSelector(props: X.PageProps) {
  const standardLocale = useLocale();
  const currentLocaleCode = props.nonStandardLocale?.code ?? standardLocale;
  const currentLocaleName = props.nonStandardLocale?.name ?? localeNames[standardLocale];
  const pathname = X.usePathname();
  const params = useParams();

  type IntlHref = {
    locale: string;
    name: string;
    href: X.PageHref;
  };

  type NonIntlHref = {
    locale: string;
    name: string;
    href: string;
  };

  const intlHrefs: IntlHref[] = props.nonStandardAltHrefs ? [] : (
    Object.entries(localeNames)
      .filter(([locale]) => locale !== currentLocaleCode)
      .map(([locale, name]) => ({
        locale,
        name,
        href: (
          props.altHrefs
            && props.altHrefs[locale as X.Locale]
            || { pathname, params } as any
        )
      }))
  );

  const nonIntlHrefs: NonIntlHref[] = X.when(
    props.nonStandardAltHrefs,
    extraHrefs => Object.values(extraHrefs).filter(({ locale }) => locale !== currentLocaleCode)
  ) ?? [];

  return {
    intlHrefs,
    nonIntlHrefs,
    currentLocaleName,
  };
}
